/**
 * The minable planet: how much it can take, how big it looks, what it is worth.
 *
 * ## Its own concept, not a boss
 *
 * A planet tier and a boss tier are both "how lucky was this encounter", and
 * that is where the similarity ends. A boss tier decides a SIZE and a Gold
 * multiplier on a thing that shoots back; a planet tier decides how many
 * Power-seconds it absorbs before it breaks. Reusing `BossTier` would have
 * saved a type and cost the ability to change either without thinking about the
 * other - so the guarantees are shared and the tables are not.
 *
 * ## Integers only
 *
 * Health in whole points, scale in basis points, Gold as a `bigint`, odds as
 * whole parts of a denominator. A percentage held as a double is a percentage
 * that does not sum to one hundred, and the tests assert that these do, by
 * equality.
 *
 * ## Bigger is worth more, and no more likely to be Prismatic
 *
 * The largest planet raises Gold, the reward budget and the number of drop
 * positions. It moves the Prismatic odds by nothing at all. That is deliberate
 * and it is the one property of this table worth defending: a rare item that
 * became common on big planets would make the size roll the real drop table,
 * and the drop table a decoration.
 */

export const MINING_PLANET_TIERS = [1, 2, 3, 4, 5, 6] as const;
export type MiningPlanetTier = (typeof MINING_PLANET_TIERS)[number];

/** Basis points: 10 000 is the reference planet, unscaled. */
export const PLANET_SCALE_DENOMINATOR_BPS = 10_000;

/** Whole parts of ten thousand. The six appearance shares sum to exactly this. */
export const PLANET_APPEARANCE_DENOMINATOR = 10_000;

/** The denominator every rare-drop chance below is expressed in. */
export const PLANET_DROP_DENOMINATOR = 1_000_000;

/**
 * The rarity ladder a component position is drawn from.
 *
 * Deliberately the SAME table the hunt's drops use, and deliberately the same
 * at every tier. There is no per-tier copy of it, because a per-tier copy is a
 * thing that can drift - and the moment the largest planet has its own ladder,
 * the size roll has quietly become the real drop table. Prismatic is 10 in a
 * million on a tier 1 planet and 10 in a million on a tier 6 one.
 */
export { MAP_1_COMPONENT_RARITY as PLANET_COMPONENT_RARITY } from '../randomness/equipment-loot';

export interface MiningPlanetTierDefinition {
  readonly tier: MiningPlanetTier;
  /** Whole health points. Absorbed at `damagePerSecond` until it reaches zero. */
  readonly maxHealth: bigint;
  /** How large it is drawn, in basis points of the reference planet. */
  readonly visualScaleBps: number;
  /**
   * The largest fraction of the stage's half-height the planet may occupy, in
   * basis points. A tier 6 that filled the window would leave nowhere to draw
   * the fleet that is mining it.
   */
  readonly maxStageFractionBps: number;
  /** Gold a confirmed destruction guarantees. Enters the ledger as a bigint. */
  readonly baseGold: bigint;
  /**
   * How many drop POSITIONS the destruction rolls. Each is an independent
   * chance at the odds below; more positions is more expected loot without
   * making any single item more likely than the table says.
   */
  readonly dropPositions: number;
  /** Parts of `PLANET_DROP_DENOMINATOR` for one position to be a stabiliser. */
  readonly stabilizerParts: number;
  /** Parts of `PLANET_DROP_DENOMINATOR` for one position to be a component. */
  readonly componentParts: number;
  /** How often this tier comes up, in parts of the appearance denominator. */
  readonly appearanceParts: number;
  /** The colour the scene lights it with. Presentation only. */
  readonly palette: string;
  /**
   * Roughly how long a 100-Power fleet takes to break it, in seconds. Derived
   * from the health and the coefficient; stored so the table can be read as a
   * design document rather than recomputed by whoever is reading it.
   */
  readonly expectedSeconds: number;
}

/**
 * How much health one point of Power removes per second.
 *
 * PENDING as a balance decision, provisional at 1. It is the single number that
 * decides how long a planet lasts, and it is configurable rather than folded
 * into the health values so the two can be tuned independently.
 *
 * Per SECOND, never per shot. The scene draws a beam per ship because that is
 * what a fleet mining a planet looks like; if it drew a hundred, the planet
 * would break at exactly the same instant.
 */
export const PLANET_DAMAGE_PER_POWER_PER_SECOND = 1n;

/**
 * The damage a fleet does over a whole number of seconds.
 *
 * Exact in `bigint`. Seconds, not milliseconds: the settlement floors to whole
 * seconds before it gets here, so a caller cannot break a planet faster by
 * settling a thousand times a second.
 */
export function planetDamageForSeconds(totalPower: bigint, seconds: bigint): bigint {
  if (totalPower <= 0n || seconds <= 0n) {
    return 0n;
  }
  return totalPower * seconds * PLANET_DAMAGE_PER_POWER_PER_SECOND;
}

/** The rate a fleet breaks a planet at, in health per second. For display. */
export function planetDamagePerSecond(totalPower: bigint): bigint {
  return totalPower <= 0n ? 0n : totalPower * PLANET_DAMAGE_PER_POWER_PER_SECOND;
}

const DEFINITIONS: Readonly<Record<MiningPlanetTier, MiningPlanetTierDefinition>> = Object.freeze({
  1: Object.freeze({
    tier: 1,
    maxHealth: 60_000n,
    visualScaleBps: 10_000,
    maxStageFractionBps: 3_400,
    baseGold: 400n,
    dropPositions: 1,
    stabilizerParts: 1_200,
    componentParts: 6_000,
    appearanceParts: 3_000,
    palette: '#67e8f9',
    expectedSeconds: 600,
  }),
  2: Object.freeze({
    tier: 2,
    maxHealth: 120_000n,
    visualScaleBps: 12_500,
    maxStageFractionBps: 3_800,
    baseGold: 900n,
    dropPositions: 2,
    stabilizerParts: 1_200,
    componentParts: 6_000,
    appearanceParts: 2_400,
    palette: '#38bdf8',
    expectedSeconds: 1_200,
  }),
  3: Object.freeze({
    tier: 3,
    maxHealth: 240_000n,
    visualScaleBps: 15_000,
    maxStageFractionBps: 4_200,
    baseGold: 2_000n,
    dropPositions: 3,
    stabilizerParts: 1_200,
    componentParts: 6_000,
    appearanceParts: 1_900,
    palette: '#a78bfa',
    expectedSeconds: 2_400,
  }),
  4: Object.freeze({
    tier: 4,
    maxHealth: 480_000n,
    visualScaleBps: 17_500,
    maxStageFractionBps: 4_600,
    baseGold: 4_400n,
    dropPositions: 4,
    stabilizerParts: 1_200,
    componentParts: 6_000,
    appearanceParts: 1_400,
    palette: '#f472b6',
    expectedSeconds: 4_800,
  }),
  5: Object.freeze({
    tier: 5,
    maxHealth: 960_000n,
    visualScaleBps: 20_000,
    maxStageFractionBps: 5_000,
    baseGold: 9_600n,
    dropPositions: 5,
    stabilizerParts: 1_200,
    componentParts: 6_000,
    appearanceParts: 900,
    palette: '#fb923c',
    expectedSeconds: 9_600,
  }),
  /**
   * The prize. Thirty-two times the first tier's health and fifty-two times its
   * Gold - drawn from exactly the same component rarity ladder, so a Prismatic
   * is no more likely here than on the smallest planet in the game.
   */
  6: Object.freeze({
    tier: 6,
    maxHealth: 1_920_000n,
    visualScaleBps: 23_000,
    maxStageFractionBps: 5_400,
    baseGold: 21_000n,
    dropPositions: 6,
    stabilizerParts: 1_200,
    componentParts: 6_000,
    appearanceParts: 400,
    palette: '#facc15',
    expectedSeconds: 19_200,
  }),
});

export function miningPlanetTierDefinition(tier: MiningPlanetTier): MiningPlanetTierDefinition {
  const definition = DEFINITIONS[tier];
  if (!definition) {
    throw new RangeError(`not a mining planet tier: ${String(tier)}`);
  }
  return definition;
}

export const MINING_PLANET_TIER_DEFINITIONS: readonly MiningPlanetTierDefinition[] = Object.freeze(
  MINING_PLANET_TIERS.map((tier) => miningPlanetTierDefinition(tier)),
);

/** The exact space a tier draw is uniform over. Never a modulo of something wider. */
export const PLANET_TIER_DRAW_SPACE = PLANET_APPEARANCE_DENOMINATOR;

/**
 * The tier a uniform draw lands on.
 *
 * Walks the table in order and subtracts, so the shares are exactly the shares
 * the table declares. A modulo of a wider space would make the low tiers
 * measurably more common, which is to say it would quietly make every planet in
 * the game worth less than the table says.
 */
export function planetTierForDraw(draw: number): MiningPlanetTier {
  if (!Number.isSafeInteger(draw) || draw < 0 || draw >= PLANET_TIER_DRAW_SPACE) {
    throw new RangeError(`planet tier draw out of range: ${String(draw)}`);
  }
  let remaining = draw;
  for (const definition of MINING_PLANET_TIER_DEFINITIONS) {
    if (remaining < definition.appearanceParts) {
      return definition.tier;
    }
    remaining -= definition.appearanceParts;
  }
  // Unreachable while the shares sum to the denominator, which a test asserts
  // by equality. Throwing beats silently handing back the rarest tier.
  throw new RangeError('planet appearance shares do not cover the draw space');
}

/**
 * How much of the stage's half-size a tier-1 planet fills, in basis points.
 *
 * Chosen so that `visualScaleBps x this` stays under every tier's own
 * `maxStageFractionBps` - a test asserts it for all six. That is what lets the
 * drawing use one multiplication instead of a special case for the largest
 * planet, which is the kind of special case that eventually draws a tier 6
 * over the fleet mining it.
 */
export const PLANET_BASE_STAGE_FRACTION_BPS = 2_340;

/**
 * The fraction of the stage's half-size this tier's planet is drawn at.
 *
 * Monotone in the tier by construction, and clamped by the tier's own ceiling
 * so the multiplication can never outgrow the table's promise.
 */
export function planetStageFractionBps(tier: MiningPlanetTier): number {
  const definition = miningPlanetTierDefinition(tier);
  const scaled = Math.round(
    (definition.visualScaleBps * PLANET_BASE_STAGE_FRACTION_BPS) / PLANET_SCALE_DENOMINATOR_BPS,
  );
  return Math.min(definition.maxStageFractionBps, scaled);
}

/** Whether a value is one of the six tiers. For reading rows back. */
export function isMiningPlanetTier(value: unknown): value is MiningPlanetTier {
  return typeof value === 'number' && MINING_PLANET_TIERS.includes(value as MiningPlanetTier);
}

/**
 * How many whole seconds of a given Power remain before the planet breaks.
 *
 * For DISPLAY. The server decides when it actually breaks, from its own clock;
 * this is what the screen counts down, and it rounds UP so it never shows zero
 * on a planet that is still standing.
 */
export function secondsToDestroy(remainingHealth: bigint, totalPower: bigint): bigint | null {
  const perSecond = planetDamagePerSecond(totalPower);
  if (perSecond <= 0n || remainingHealth <= 0n) {
    return null;
  }
  return (remainingHealth + perSecond - 1n) / perSecond;
}
